import { useState } from 'react';
import './DevMenu.css';
import { env } from '../../core/util/env';
import {
  type DevOverrides,
  loadDevOverrides,
  saveDevOverrides,
  clearDevOverrides,
} from '../../core/util/devOverrides';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import { Locale } from '../../core/locale/types';
import rememberMe from '../../lib/clients/remember-me-storage';
import { useFeatureFlags } from '../FeatureFlagsContext';
import { FLAGS, FLAG_NAMES } from '../registry';

const isEnabled = env.DEV || env.UAT;

const DEVICE_TRAITS = ['isMobile', 'isAndroid', 'isWebView'] as const;
const IFRAME_TRAITS = ['isEmbedded', 'isWidgetSdkEmbed'] as const;
const CONFIG_KEYS = [
  'VITE_API_HOST',
  'VITE_LP_SECONDARY_URL',
  'VITE_FEATURE_REDESIGN_SAMPLE_RATE',
  'VITE_FEATURE_SKIPPY_SAMPLE_RATE',
] as const;

const TriState = ({
  value,
  onChange,
}: {
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
}) => (
  <div className="dev-menu-controls">
    <button type="button" className={value === true ? 'active' : ''} onClick={() => onChange(true)}>
      On
    </button>
    <button
      type="button"
      className={value === false ? 'active' : ''}
      onClick={() => onChange(false)}
    >
      Off
    </button>
    <button
      type="button"
      className={value === undefined ? 'active' : ''}
      onClick={() => onChange(undefined)}
    >
      Auto
    </button>
  </div>
);

const DevMenuPanel = (): React.ReactNode => {
  const [open, setOpen] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [dev, setDev] = useState<DevOverrides>(() => loadDevOverrides());
  const { resolved, overrides, setOverride, resetOverrides } = useFeatureFlags();
  const { locale, setLocale } = useLanguageContext();

  const persist = (next: DevOverrides) => {
    saveDevOverrides(next);
    setDev(next);
    setNeedsReload(true);
  };

  const setTrait = (group: 'device' | 'iframe', key: string, value: boolean | undefined) => {
    const nextGroup: Record<string, boolean> = { ...(dev[group] as Record<string, boolean>) };
    if (value === undefined) {
      delete nextGroup[key];
    } else {
      nextGroup[key] = value;
    }
    persist({ ...dev, [group]: Object.keys(nextGroup).length ? nextGroup : undefined });
  };

  const setConfig = (key: string, value: string) => {
    const nextConfig: Record<string, string> = { ...dev.config };
    if (value === '') {
      delete nextConfig[key];
    } else {
      nextConfig[key] = value;
    }
    persist({ ...dev, config: Object.keys(nextConfig).length ? nextConfig : undefined });
  };

  // Read live at submit time, so unlike the other overrides this one needs no reload.
  const toggleCrashAfterSubmit = () => {
    const next = { ...dev, crashAfterSubmit: dev.crashAfterSubmit ? undefined : true };
    saveDevOverrides(next);
    setDev(next);
  };

  const resetAll = () => {
    resetOverrides();
    clearDevOverrides();
    setDev({});
    setNeedsReload(true);
  };

  const overrideCount =
    Object.keys(overrides).length +
    Object.keys(dev.device ?? {}).length +
    Object.keys(dev.iframe ?? {}).length +
    Object.keys(dev.config ?? {}).length +
    (dev.crashAfterSubmit ? 1 : 0);

  return (
    <div className="dev-menu">
      <button
        type="button"
        className={`dev-menu-trigger${overrideCount ? ' has-overrides' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        🚩 dev
        {overrideCount > 0 && (
          <span className="dev-menu-badge" title={`${overrideCount} override(s) active`}>
            {overrideCount}
          </span>
        )}
      </button>
      {open && (
        <div className="dev-menu-panel" role="dialog" aria-label="Dev menu">
          <div className="dev-menu-header">
            <span>Dev menu ({env.MODE})</span>
            <button type="button" onClick={resetAll}>
              Reset all
            </button>
          </div>

          {needsReload && (
            <div className="dev-menu-reload">
              Environment/config change — reload to apply.
              <button type="button" onClick={() => window.location.reload()}>
                Reload
              </button>
            </div>
          )}

          <p className="dev-menu-section">Feature flags</p>
          <ul className="dev-menu-list">
            {FLAG_NAMES.map((name) => {
              const override = overrides[name];
              const value = override ?? resolved[name];
              return (
                <li key={name} className="dev-menu-item">
                  <div className="dev-menu-item-head">
                    <span className={`dev-menu-name ${value ? 'on' : 'off'}`}>{name}</span>
                    <span className="dev-menu-source">
                      {value ? 'on' : 'off'} · {override === undefined ? 'auto' : 'override'}
                    </span>
                  </div>
                  <p className="dev-menu-desc">{FLAGS[name].description}</p>
                  <TriState value={override} onChange={(v) => setOverride(name, v)} />
                </li>
              );
            })}
          </ul>

          <p className="dev-menu-section">Device</p>
          <ul className="dev-menu-list">
            {DEVICE_TRAITS.map((trait) => (
              <li key={trait} className="dev-menu-item">
                <div className="dev-menu-item-head">
                  <span className="dev-menu-name">{trait}</span>
                </div>
                <TriState
                  value={dev.device?.[trait]}
                  onChange={(v) => setTrait('device', trait, v)}
                />
              </li>
            ))}
          </ul>

          <p className="dev-menu-section">Embedding (iframe)</p>
          <ul className="dev-menu-list">
            {IFRAME_TRAITS.map((trait) => (
              <li key={trait} className="dev-menu-item">
                <div className="dev-menu-item-head">
                  <span className="dev-menu-name">{trait}</span>
                </div>
                <TriState
                  value={dev.iframe?.[trait]}
                  onChange={(v) => setTrait('iframe', trait, v)}
                />
              </li>
            ))}
          </ul>

          <p className="dev-menu-section">Config</p>
          <ul className="dev-menu-list">
            {CONFIG_KEYS.map((key) => (
              <li key={key} className="dev-menu-item">
                <label className="dev-menu-name" htmlFor={`dev-cfg-${key}`}>
                  {key}
                </label>
                <input
                  id={`dev-cfg-${key}`}
                  className="dev-menu-input"
                  value={dev.config?.[key] ?? ''}
                  placeholder={String((env as Record<string, unknown>)[key] ?? '')}
                  onChange={(e) => setConfig(key, e.target.value)}
                />
              </li>
            ))}
          </ul>

          <p className="dev-menu-section">Locale</p>
          <div className="dev-menu-controls">
            {Object.entries(Locale).map(([label, value]) => (
              <button
                key={value}
                type="button"
                className={locale === value ? 'active' : ''}
                onClick={() => setLocale(value as Locale)}
              >
                {label}
              </button>
            ))}
          </div>

          <p className="dev-menu-section">Sentry</p>
          <div className="dev-menu-controls">
            <button
              type="button"
              className={dev.crashAfterSubmit ? 'active' : ''}
              onClick={toggleCrashAfterSubmit}
            >
              💥 Crash after submit
            </button>
          </div>

          <p className="dev-menu-section">Storage</p>
          <div className="dev-menu-controls">
            <button type="button" onClick={() => rememberMe.clear()}>
              Clear remember-me
            </button>
            <button
              type="button"
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
            >
              Clear all + reload
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const DevMenu = (): React.ReactNode => {
  if (!isEnabled) {
    return null;
  }
  return <DevMenuPanel />;
};
